import { useCallback } from 'react';

import { Button } from '@opendoor/atoms/button';
import { Popover } from '@opendoor/atoms/popover';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronDown } from '@tabler/icons-react';
import {
  type BedroomFilterValue,
  BedroomsBathroomsFilter,
} from 'components/marketplace/search/BedroomsBathroomsFilter';
import {
  MARKETPLACE_POPOVER_SURFACE_CLASS,
  type PopoverTriggerRender,
} from 'components/marketplace/shared/popoverStyles';

const filterValue = (value: string): string => (value === '0' ? '' : value);

export interface QuickBedroomsBathroomsValue {
  bathrooms: string;
  bedrooms: string;
  maxBedrooms: string;
}

export const quickBedroomsBathroomsLabel = ({
  bedrooms,
  maxBedrooms,
  bathrooms,
}: QuickBedroomsBathroomsValue): string => {
  const normalizedBedrooms = filterValue(bedrooms);
  const normalizedMaxBedrooms = filterValue(maxBedrooms);
  const normalizedBathrooms = filterValue(bathrooms);
  const bedroomLabel = normalizedBedrooms
    ? `${normalizedBedrooms}${normalizedMaxBedrooms === normalizedBedrooms ? '' : '+'} bd`
    : '';

  if (bedroomLabel && normalizedBathrooms) {
    return `${bedroomLabel}, ${normalizedBathrooms}+ ba`;
  }
  if (bedroomLabel) {
    return bedroomLabel;
  }
  if (normalizedBathrooms) {
    return `${normalizedBathrooms}+ ba`;
  }
  return 'Beds/Baths';
};

interface QuickBedroomsBathroomsFilterProps {
  bathrooms: string;
  bedrooms: string;
  maxBedrooms: string;
  onChange: (value: QuickBedroomsBathroomsValue) => void;
}

export const QuickBedroomsBathroomsFilter = ({
  bathrooms,
  bedrooms,
  maxBedrooms,
  onChange,
}: QuickBedroomsBathroomsFilterProps) => {
  const normalizedBedrooms = filterValue(bedrooms);
  const normalizedMaxBedrooms = filterValue(maxBedrooms);
  const normalizedBathrooms = filterValue(bathrooms);
  const label = quickBedroomsBathroomsLabel({
    bedrooms: normalizedBedrooms,
    maxBedrooms: normalizedMaxBedrooms,
    bathrooms: normalizedBathrooms,
  });

  const handleBedroomsChange = useCallback(
    ({ minBeds, maxBeds }: BedroomFilterValue) => {
      onChange({
        bedrooms: filterValue(minBeds),
        maxBedrooms: filterValue(maxBeds),
        bathrooms: normalizedBathrooms,
      });
    },
    [normalizedBathrooms, onChange],
  );

  const handleBathroomsChange = useCallback(
    (value: string) => {
      onChange({
        bedrooms: normalizedBedrooms,
        maxBedrooms: normalizedMaxBedrooms,
        bathrooms: filterValue(value),
      });
    },
    [normalizedBedrooms, normalizedMaxBedrooms, onChange],
  );

  const renderTrigger = useCallback<PopoverTriggerRender>(
    (props) => (
      <Button
        {...props}
        analyticsName="marketplace-bedrooms-bathrooms-quick-filter"
        aria-label={`Beds and baths filter: ${label}`}
        size="sm"
        variant="secondary"
        className="h-10 shrink-0 px-3"
      >
        {label}
        <IconChevronDown size={16} aria-hidden />
      </Button>
    ),
    [label],
  );

  return (
    <Popover.Root>
      <Popover.Trigger render={renderTrigger} />
      <Popover align="start" className={cn('w-80 p-4', MARKETPLACE_POPOVER_SURFACE_CLASS)}>
        <BedroomsBathroomsFilter
          minBeds={normalizedBedrooms || '0'}
          maxBeds={normalizedMaxBedrooms || '0'}
          minBaths={normalizedBathrooms || '0'}
          handleBedroomsChange={handleBedroomsChange}
          handleMinBathsChange={handleBathroomsChange}
        />
      </Popover>
    </Popover.Root>
  );
};
